import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge } from '@/components/ui';
import type { VendorMatch } from '../../../services/vendorService';

const STATUS_LABEL: Record<string, string> = { PENDING: 'Pending', ACTIVE: 'Active', INACTIVE: 'Inactive' };

/**
 * H7 - vendors whose names collide with, or are close to, this one, shown to the approver so
 * a duplicate is caught before activation. `reason` is what the creator gave for going ahead
 * anyway.
 */
const PossibleMatches: React.FC<{
  matches?: VendorMatch[] | null;
  reason?: string | null;
  /** Table-cell form: a short list, no surrounding alert. */
  compact?: boolean;
}> = ({ matches, reason, compact }) => {
  const list = matches ?? [];
  if (!list.length) {
    return compact ? <span className="tw-text-content-muted">None found</span> : null;
  }

  const items = (
    <ul className={`tw-mb-0 tw-pl-4 ${compact ? 'tw-text-sm' : ''}`}>
      {list.map((m) => (
        <li key={m.id}>
          <Link to={`/vendors/${m.id}`} onClick={(e) => e.stopPropagation()}>
            {m.name}
          </Link>
          {m.legalName && m.legalName !== m.name && <span className="tw-text-content-muted"> ({m.legalName})</span>}{' '}
          <Badge bg={m.exact ? 'danger' : 'secondary'}>{m.exact ? 'Same name' : 'Similar'}</Badge>{' '}
          <span className="tw-text-content-muted">{STATUS_LABEL[m.status] ?? m.status}</span>
        </li>
      ))}
    </ul>
  );

  if (compact) {
    return (
      <div>
        {items}
        {reason && <div className="tw-mt-1 tw-text-sm tw-text-content-secondary">Reason given: {reason}</div>}
      </div>
    );
  }

  return (
    <Alert variant="warning" className="tw-mb-3">
      <div className="tw-mb-1 tw-font-medium">Possible duplicate vendors – check before activating</div>
      {items}
      {reason && <div className="tw-mt-2">Reason given when this vendor was saved: {reason}</div>}
    </Alert>
  );
};

export default PossibleMatches;
